import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { Organization } from '../../../models';

import { Menu, NavMenu, NavMenuBody, NavMenuItem, Button, ContextMenu, DropdownItem, CopyToClipboardLink } from '@weezevent/nacre';
import OrganizationModal from '../../../pages/organizations/OrganizationModal';
import { SwitchLoadingOptions } from '../../loadingCard';

import css from './sidebar.module.css';
import useAppContext from 'context/useAppContext';

const SideBarMenu = ({ organizationId, capacities, roles = [] }) => {
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);

    const scopedCapacities = React.useMemo(() => {
        if (!organizationId) {
            return capacities;
        }
        return new Set(
            roles
                .filter(role => (role.context.level === 3 && role.context.value === String(organizationId)) || role.context.level === 0)
                .flatMap(role => role.role.capacities.map(cap => cap.slug))
        );
    }, [roles, organizationId, capacities]);

    const Component = React.useMemo(() => {
        if (!organizationId) {
            return RootMenu;
        }
        return OrgaMenu;
    }, [organizationId]);

    return (
        <div className={css['menu-orga']}>
            {window.weezConfig.enableCreateOrganization && (
                <>
                    <OrganizationModal open={open} onClose={() => setOpen(false)} />
                    <div className={css['create-orga-btn']}>
                        <Button fluid primary onClick={() => setOpen(true)}>
                            {t(`sauron.organizations.error.create`)}
                        </Button>
                    </div>
                </>
            )}
            <Component capacities={scopedCapacities} organizationId={organizationId} />
        </div>
    );
};

export const RootMenu = ({ capacities }) => {
    const { t } = useTranslation();

    return (
        <Menu useNew capacities={Array.from(capacities)} alternative>
            <NavMenu>
                <NavMenuItem icon="home">{t('common.organizations')}</NavMenuItem>
                <NavMenuBody>
                    <NavMenuItem to={'/organizations'} exact>
                        {t('common.organizations')}
                    </NavMenuItem>
                    <NavMenuItem to={'/events'} exact allowedCapacities={['PyvarCapacity.EVENT']}>
                        {t('common.events')}
                    </NavMenuItem>
                </NavMenuBody>
            </NavMenu>

            <NavMenu allowedCapacities={['RoleCapacity.ADMIN', 'UserCapacity.ROLE_ADMIN']}>
                <NavMenuItem icon="users">{t('common.users')}</NavMenuItem>
                <NavMenuBody>
                    <NavMenuItem to={'/users'} exact>
                        {t('common.users')}
                    </NavMenuItem>
                    <NavMenuItem to={'/roles'} exact allowedCapacities={['UserCapacity.ROLE_ADMIN']}>
                        {t('common.roles')}
                    </NavMenuItem>
                </NavMenuBody>
            </NavMenu>

            <NavMenu allowedCapacities={['EmailCapacity.DOMAIN', 'EmailCapacity.DOMAIN_ADMIN', 'EmailCapacity.BLACKLIST']}>
                <NavMenuItem icon="envelope">Emails</NavMenuItem>
                <NavMenuBody>
                    <NavMenuItem to={'/emails/blacklist'} exact allowedCapacities={['EmailCapacity.BLACKLIST']}>
                        {t('sauron.emails.blacklist.title')}
                    </NavMenuItem>
                    <NavMenuItem to={'/emails/domains'} exact allowedCapacities={['EmailCapacity.DOMAIN', 'EmailCapacity.DOMAIN_ADMIN']}>
                        {t('sauron.emails.domains.title')}
                    </NavMenuItem>
                    <NavMenuItem to={'/emails/templates'} allowedCapacities={['EmailCapacity.TEMPLATE_ADMIN', 'EmailCapacity.TEMPLATE']}>
                        {t('sauron.emails.templates')}
                    </NavMenuItem>
                </NavMenuBody>
            </NavMenu>

            <NavMenu allowedCapacities={['RoleCapacity.ADMIN']}>
                <NavMenuItem icon="plug" allowedCapacities={['PookCapacity.WEBHOOKS_ADMIN', 'PookCapacity.FORMATTERS_ADMIN', 'PookCapacity.API_ACCESS_ADMIN']}>
                    {t('common.integrations')}
                </NavMenuItem>
                <NavMenuBody>
                    <NavMenuItem to={{ pathname: '/tools/webhooks', search: '?active=true&archived=false&page=0' }} exact allowedCapacities={['PookCapacity.WEBHOOKS_ADMIN']}>
                        {t('common.webhooks')}
                    </NavMenuItem>
                    <NavMenuItem to={'/tools/formatters'} exact allowedCapacities={['PookCapacity.FORMATTERS_ADMIN']}>
                        {t('common.formatters')}
                    </NavMenuItem>
                    <NavMenuItem to={{ pathname: '/applications', search: '?same_level_users=false&page=0' }} exact allowedCapacities={['PookCapacity.API_ACCESS_ADMIN']}>
                        {t('common.applications')}
                    </NavMenuItem>
                    <NavMenuItem to={'/applications/legacy'} exact allowedCapacities={['PookCapacity.API_ACCESS_ADMIN']}>
                        {t('common.applications-legacy')}
                    </NavMenuItem>
                </NavMenuBody>
            </NavMenu>
        </Menu>
    );
};

const OrgaMenu = ({ capacities, organizationId }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [search, setSearch] = React.useState('');
    const [, setContext] = useAppContext();

    const [currentOrganization] = Organization.useApiModel(
        {
            id: organizationId,
            strict: true,
            cache: false,
            allow_cache: false,
            launch: Boolean(organizationId),
            onSuccess: response => {
                setContext({ organization: response });
            }
        },
        [organizationId]
    );

    const [organizations, loadingOrganizations] = Organization.useApiModel(
        {
            cache: false,
            allow_cache: false,
            // Limit to 50 organizers to enhance performances
            // If a user has access to more than 50 organizations, the component provides a searchbar
            query: { search, limit: 50 }
        },
        [search]
    );

    const selectItems = React.useMemo(() => {
        if (loadingOrganizations) {
            return SwitchLoadingOptions;
        }
        return organizations?.map(org => (
            <div key={org.pk}>
                <DropdownItem onClick={() => history.push(`/organizations/O${org.id}/users`)}>{org.name || t('sauron.organizations.organization_without_name')}</DropdownItem>
            </div>
        ));
    }, [organizations, loadingOrganizations]);

    return (
        <>
            <ContextMenu
                catchphrase={
                    <CopyToClipboardLink tooltipMini value={organizationId}>
                        {`${t('common.organization')} • O ${organizationId}`}
                    </CopyToClipboardLink>
                }
                title={currentOrganization?.name || t('sauron.organizations.organization_without_name')}
                switchMenuProps={{
                    text: t('sauron.organizations.search'),
                    items: selectItems,
                    searchProps: {
                        placeholder: t('common.search'),
                        onChange: (_, value) => setSearch(value),
                        handleReset: () => setSearch('')
                    }
                }}
            />

            <Menu useNew alternative capacities={Array.from(capacities)}>
                <NavMenu>
                    <NavMenuItem icon="home" to={`/organizations/O${organizationId}/events`} allowedCapacities={['RoleCapacity.ADMIN', 'RoleCapacity.OWNER']}>
                        {t('common.events')}
                    </NavMenuItem>
                </NavMenu>
                <NavMenu>
                    <NavMenuItem icon="users" to={`/organizations/O${organizationId}/users`} allowedCapacities={['RoleCapacity.ADMIN', 'RoleCapacity.OWNER']}>
                        {t('common.users')}
                    </NavMenuItem>
                </NavMenu>
                <NavMenu allowedCapacities={['EmailCapacity.IDENTITY_ADMIN', 'EmailCapacity.IDENTITY', 'EmailCapacity.DOMAIN', 'EmailCapacity.DOMAIN_ADMIN']}>
                    <NavMenuItem icon="envelope">Emails</NavMenuItem>
                    <NavMenuBody>
                        <NavMenuItem
                            to={`/organizations/O${organizationId}/emails/identities`}
                            exact
                            allowedCapacities={['EmailCapacity.IDENTITY_ADMIN', 'EmailCapacity.IDENTITY']}
                        >
                            {t('sauron.emails.identities.title')}
                        </NavMenuItem>
                        <NavMenuItem to={`/organizations/O${organizationId}/emails/domains`} exact allowedCapacities={['EmailCapacity.DOMAIN', 'EmailCapacity.DOMAIN_ADMIN']}>
                            {t('sauron.emails.domains.title')}
                        </NavMenuItem>
                        <NavMenuItem to={`/organizations/O${organizationId}/emails/templates`} allowedCapacities={['PyvarCapacity.DEV']}>
                            {t('sauron.emails.templates')}
                        </NavMenuItem>
                    </NavMenuBody>
                </NavMenu>

                <NavMenu>
                    <NavMenuItem icon="money" to={`/organizations/O${organizationId}/finance`} allowedCapacities={['finance-entities-can-see-config-page']}>
                        {t('common.financeModule')}
                    </NavMenuItem>
                </NavMenu>
                <NavMenu allowedCapacities={['RoleCapacity.OWNER']}>
                    <NavMenuItem
                        icon="plug"
                        allowedCapacities={[
                            'PookCapacity.WEBHOOKS',
                            'PookCapacity.WEBHOOKS_ADMIN',
                            'PookCapacity.FORMATTERS',
                            'PookCapacity.FORMATTERS_ADMIN',
                            'PookCapacity.API_ACCESS',
                            'PookCapacity.API_ACCESS_ADMIN'
                        ]}
                    >
                        {t('common.integrations')}
                    </NavMenuItem>
                    <NavMenuBody>
                        <NavMenuItem to={`/organizations/O${organizationId}/tools/webhooks`} allowedCapacities={['PookCapacity.WEBHOOKS', 'PookCapacity.WEBHOOKS_ADMIN']}>
                            {t('common.webhooks')}
                        </NavMenuItem>
                        <NavMenuItem to={`/organizations/O${organizationId}/tools/formatters`} allowedCapacities={['PookCapacity.FORMATTERS', 'PookCapacity.FORMATTERS_ADMIN']}>
                            {t('common.formatters')}
                        </NavMenuItem>
                        <NavMenuItem to={`/organizations/O${organizationId}/applications`} allowedCapacities={['PookCapacity.API_ACCESS', 'PookCapacity.API_ACCESS_ADMIN']}>
                            {t('common.applications')}
                        </NavMenuItem>
                        <NavMenuItem to={`/organizations/O${organizationId}/applications/legacy`} allowedCapacities={['PookCapacity.API_ACCESS', 'PookCapacity.API_ACCESS_ADMIN']}>
                            {t('common.applications-legacy')}
                        </NavMenuItem>
                    </NavMenuBody>
                </NavMenu>
            </Menu>
        </>
    );
};

export default SideBarMenu;
